import {Animated, View, ViewStyle} from 'react-native';
import React, {useCallback, useRef} from 'react';
import {images} from '../../../assets/icons';
import {useStyles} from '../../hooks/styles';
import {Props} from './model';

const DEFAULT_CONTAINER_WIDTH = 190;
const DEFAULT_CONTAINER_HEIGHT = 120;
const DEFAULT_IMAGE_WIDTH = 10;
const DEFAULT_IMAGE_SPACING = 20;

const itemsCount = images.Loader.length;
const range = images.Loader.map((_, i) => i);
const startAnimValue = 0;
const endAnimValue = 10;
const itemsHeight = [40, 60, 80, 120, 80, 60, 40];

const getItemGroupByIndex = (index: number): 1 | 2 | 3 => {
  const middleIndex = (itemsCount - 1) / 2;

  if (index === middleIndex) {
    return 2;
  }

  return index < middleIndex ? 1 : 3;
};

export default ({size = 100}: Props) => {
  const duration = 600;
  const framesAnim = useRef(new Animated.Value(startAnimValue)).current;

  const runAnimation = useCallback((isForwardDirection: boolean) => {
    framesAnim.setValue(isForwardDirection ? startAnimValue : endAnimValue);
    Animated.timing(framesAnim, {
      toValue: isForwardDirection ? endAnimValue : startAnimValue,
      useNativeDriver: false,
      duration,
      easing: (v) => {
        return Math.floor(v * itemsCount) / itemsCount;
      },
    }).start(() => runAnimation(!isForwardDirection));
  }, []);

  React.useEffect(() => {
    runAnimation(true);
  }, []);

  const styles = useStyles(() => ({
    image: {
      position: 'absolute',
      width: DEFAULT_IMAGE_WIDTH,
    },
    container: {
      position: 'relative',
    },
  }));

  const scale = size / DEFAULT_CONTAINER_WIDTH;

  const getOpacity = React.useCallback(
    (itemIndex: number) => {
      const inputStep = endAnimValue / itemsCount;
      const inputShift = inputStep * itemIndex;
      const outputStep = 1 / itemsCount;
      const outputShift = outputStep * itemIndex;

      return framesAnim.interpolate({
        inputRange: [startAnimValue, endAnimValue - inputShift, endAnimValue],
        outputRange: [0 + outputShift, 1, 1 - outputShift],
      });
    },
    [framesAnim],
  );

  const getBottom = React.useCallback(
    (itemIndex: number) => {
      const itemGroup = getItemGroupByIndex(itemIndex);

      if (itemGroup === 2) {
        return 0;
      }

      const bottomMultiplier = itemGroup === 1 ? 1 : -1;
      const baseBottom = (itemIndex + (itemGroup === 1 ? 0 : 1)) % 2 ? 20 : 10;

      return framesAnim.interpolate({
        inputRange: [startAnimValue, endAnimValue],
        outputRange: [baseBottom + startAnimValue * bottomMultiplier, baseBottom + endAnimValue * bottomMultiplier],
      });
    },
    [framesAnim],
  );

  return (
    <View
      style={[
        styles.container,
        {width: DEFAULT_CONTAINER_WIDTH, height: DEFAULT_CONTAINER_HEIGHT, transform: [{scale}]},
      ]}>
      {range.map((item, index) => {
        const Image = images.Loader[item];

        const imageStyles: Animated.WithAnimatedObject<ViewStyle> = {
          opacity: getOpacity(item),
          left: index * (DEFAULT_IMAGE_WIDTH + DEFAULT_IMAGE_SPACING),
          bottom: getBottom(index),
          height: itemsHeight[index],
        };

        return (
          <Animated.View style={[styles.image, imageStyles]} key={item}>
            <Image />
          </Animated.View>
        );
      })}
    </View>
  );
};
